import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Trash2, Download, Eye, AlertTriangle, Pencil, Save, X, FileBarChart, Volume2, Square } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSpeech } from "../hooks/useSpeech";

const STATUS = {
  scanning: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  scanned: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40",
  complete: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  failed: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function History() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setL] = useState(true);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const { speakText, stop, speaking } = useSpeech();

  const playPreview = (d) => {
    if (playingId === d.id) { stop(); setPlayingId(null); return; }
    stop();
    const preview = `${d.fileName}. ${d.originalWordCount?.toLocaleString() || 0} words. ${d.finalAiPercentage ?? 0}% AI detected. Status: ${d.status}.`;
    setPlayingId(d.id);
    speakText(preview.slice(0, 200));
  };

  useEffect(() => {
    if (!speaking) setPlayingId(null);
  }, [speaking]);

  const load = async () => {
    setL(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } finally { setL(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Deleted");
    load();
  };

  const dl = async (id, kind) => {
    const path = kind === "report" ? `/documents/${id}/report` : `/documents/${id}/download?version=${kind}`;
    const { data } = await api.get(path);
    let blob;
    if (data.encoding === "base64") {
      const bytes = atob(data.content);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      blob = new Blob([arr], { type: data.mimeType });
    } else {
      blob = new Blob([data.content], { type: "text/plain" });
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = data.fileName;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const startRename = (d) => { setRenameId(d.id); setRenameVal(d.fileName); };
  const saveRename = async (id) => {
    if (!renameVal.trim()) return;
    try {
      await api.put(`/documents/${id}/rename`, { fileName: renameVal.trim() });
      setRenameId(null);
      load();
      toast.success("Renamed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Rename failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-14">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Library</div>
      <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Document history</h1>

      {user?.plan === "free" && docs.length >= 3 && (
        <div className="mt-6 rounded-xl p-4 flex items-start gap-3 text-sm border border-amber-500/40 bg-amber-500/10">
          <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5"/>
          <div className="flex-1 text-amber-200">
            You are on the Free plan. Only your 3 most recent documents are shown.{" "}
            <Link to="/pricing" className="text-amber-300 hover:underline font-medium">Upgrade to Pro for full history.</Link>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="mt-8 glass-strong rounded-2xl overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-zinc-400 bg-white/[0.02]">
            <tr>
              <th className="text-left px-5 py-3">File</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">Words</th>
              <th className="text-left px-3 py-3">Rounds</th>
              <th className="text-left px-3 py-3">AI%</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-5 py-12 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && docs.length === 0 && <tr><td colSpan="7" className="px-5 py-12 text-center text-zinc-500">No documents yet. <Link to="/dashboard" className="text-indigo-300 hover:underline">Upload one →</Link></td></tr>}
            {docs.map(d => (
              <tr key={d.id} className="border-t border-white/[0.05] hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  {renameId === d.id ? (
                    <div className="flex items-center gap-1.5">
                      <Input data-testid={`history-rename-input-${d.id}`} value={renameVal} onChange={e=>setRenameVal(e.target.value)} className="h-8 text-xs"/>
                      <button onClick={()=>saveRename(d.id)} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300" data-testid={`history-rename-save-${d.id}`}><Save size={13}/></button>
                      <button onClick={()=>setRenameId(null)} className="p-1.5 rounded hover:bg-white/10"><X size={13}/></button>
                    </div>
                  ) : (
                    <Link to={`/document/${d.id}`} className="flex items-center gap-2 hover:text-indigo-300" data-testid={`history-row-${d.id}`}>
                      <FileText size={14} className="text-zinc-500"/>
                      <span className="truncate max-w-[260px]">{d.fileName}</span>
                    </Link>
                  )}
                </td>
                <td className="px-3 py-3 uppercase text-xs text-zinc-400">{d.fileType}</td>
                <td className="px-3 py-3">{d.originalWordCount?.toLocaleString()}</td>
                <td className="px-3 py-3">{(d.rounds || []).length}</td>
                <td className="px-3 py-3 font-mono">{d.finalAiPercentage ?? "—"}%</td>
                <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded border ${STATUS[d.status] || STATUS.scanned}`}>{d.status}</span></td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => playPreview(d)} className={`p-1.5 rounded hover:bg-white/10 ${playingId === d.id ? "text-indigo-400" : ""}`} title={playingId === d.id ? "Stop" : "Listen to preview"}>
                      {playingId === d.id ? <Square size={13}/> : <Volume2 size={14}/>}
                    </button>
                    <Link to={`/document/${d.id}`} className="p-1.5 rounded hover:bg-white/10" title="View" data-testid={`history-view-${d.id}`}><Eye size={14}/></Link>
                    <button onClick={() => startRename(d)} className="p-1.5 rounded hover:bg-white/10" title="Rename" data-testid={`history-rename-${d.id}`}><Pencil size={13}/></button>
                    <button onClick={() => dl(d.id, "paraphrased")} className="p-1.5 rounded hover:bg-white/10" title="Download humanized" data-testid={`history-download-${d.id}`}><Download size={14}/></button>
                    <button onClick={() => dl(d.id, "report")} className="p-1.5 rounded hover:bg-white/10" title="Download report" data-testid={`history-report-${d.id}`}><FileBarChart size={14}/></button>
                    <button onClick={() => del(d.id)} className="p-1.5 rounded hover:bg-red-500/20 text-red-400" title="Delete" data-testid={`history-delete-${d.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-6 md:hidden space-y-3">
        {loading && <div className="text-center text-zinc-500 py-8">Loading…</div>}
        {!loading && docs.length === 0 && <div className="text-center text-zinc-500 py-8">No documents yet. <Link to="/dashboard" className="text-indigo-300">Upload one →</Link></div>}
        {docs.map(d => (
          <div key={d.id} className="glass-strong rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <FileText size={16} className="text-indigo-300 mt-0.5"/>
              <div className="flex-1 min-w-0">
                {renameId === d.id ? (
                  <div className="flex items-center gap-1.5">
                    <Input value={renameVal} onChange={e=>setRenameVal(e.target.value)} className="h-8 text-xs"/>
                    <button onClick={()=>saveRename(d.id)} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300"><Save size={13}/></button>
                    <button onClick={()=>setRenameId(null)} className="p-1.5 rounded hover:bg-white/10"><X size={13}/></button>
                  </div>
                ) : (
                  <Link to={`/document/${d.id}`} className="font-medium truncate block hover:text-indigo-300">{d.fileName}</Link>
                )}
                <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-zinc-400">
                  <span className="uppercase">{d.fileType}</span>
                  <span>·</span>
                  <span>{d.originalWordCount?.toLocaleString()}w</span>
                  <span>·</span>
                  <span>{(d.rounds||[]).length} rounds</span>
                  <span>·</span>
                  <span className="font-mono">{d.finalAiPercentage ?? "—"}% AI</span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
              <Link to={`/document/${d.id}`} className="flex-1 min-w-[80px]"><Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700"><Eye size={13} className="mr-1.5"/>View</Button></Link>
              <button onClick={() => playPreview(d)} className={`p-2 rounded glass ${playingId === d.id ? "text-indigo-400" : ""}`} title={playingId === d.id ? "Stop" : "Listen to preview"}>
                {playingId === d.id ? <Square size={13}/> : <Volume2 size={13}/>}
              </button>
              <button onClick={() => startRename(d)} className="p-2 rounded glass" title="Rename"><Pencil size={13}/></button>
              <button onClick={() => dl(d.id, "paraphrased")} className="p-2 rounded glass" title="Download humanized"><Download size={13}/></button>
              <button onClick={() => dl(d.id, "report")} className="p-2 rounded glass" title="Report"><FileBarChart size={13}/></button>
              <button onClick={() => del(d.id)} className="p-2 rounded glass text-red-400" title="Delete"><Trash2 size={13}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
